export type EditorialFieldType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'checkboxes'
  | 'datetime-local'
  | 'date'
  | 'number'
  | 'people';

export interface EditorialWizardField {
  id: string;
  label: string;
  type: EditorialFieldType;
  required?: boolean;
  wide?: boolean;
  single?: boolean;
  minPeople?: number;
  options?: string[];
  placeholder?: string;
  value?: string;
  suggestion?: string | string[];
  locked?: boolean;
  min?: number;
  max?: number;
}

export interface EditorialWizardStep {
  id: string;
  title: string;
  desc: string;
  fields: EditorialWizardField[];
}

const f = (
  id: string,
  label: string,
  type: EditorialFieldType = 'text',
  opts: Partial<Omit<EditorialWizardField, 'id' | 'label' | 'type'>> = {}
): EditorialWizardField => ({ id, label, type, ...opts });

export const CONFERENCE_STEPS: EditorialWizardStep[] = [
  {
    id: 'identity',
    title: 'Проект и организатор',
    desc: 'Вид, уровень, официальное название, цель и ответственные лица.',
    fields: [
      f('eventType', 'Вид мероприятия', 'select', {
        required: true,
        options: [
          'Научная конференция',
          'Семинар',
          'Симпозиум',
          'Вебинар',
          'Научный семинар',
          'Научно-методический семинар',
          'Форум',
          'Круглый стол',
          'Конкурс научных работ',
          'Собственный вариант',
        ],
        suggestion: 'Научная конференция',
      }),
      f('eventLevel', 'Уровень мероприятия', 'select', {
        required: true,
        options: [
          'Собственный вариант',
          'Международный',
          'Республиканский',
          'Региональный',
          'Областной',
          'Городской',
          'Вузовский',
        ],
      }),
      f('titleRu', 'Полное официальное название (RU)', 'text', {
        required: true,
        wide: true,
        placeholder: 'Например: Международная научная конференция «...»',
      }),
      f('titleKz', 'Полное официальное название (KZ)', 'text', { wide: true }),
      f('titleEn', 'Полное официальное название (EN)', 'text', { wide: true }),
      f('acronym', 'Краткое название / акроним', 'text'),
      f('description', 'Краткое описание и цель', 'textarea', { required: true, wide: true }),
      f('organizer', 'Основной организатор', 'text', { required: true, locked: true }),
      f('contact', 'Официальный контакт', 'text', { required: true, locked: true }),
    ],
  },
  {
    id: 'science',
    title: 'Научная область и аудитория',
    desc: 'Предметная область, направления, секции, ключевые слова и языки.',
    fields: [
      f('scienceArea', 'Основная область науки', 'select', {
        required: true,
        options: [
          'Собственный вариант',
          'Гуманитарные науки',
          'Социальные науки',
          'Педагогика и образование',
          'Филология и лингвистика',
          'Информационные технологии',
        ],
      }),
      f('directions', 'Научные направления', 'checkboxes', {
        required: true,
        wide: true,
        options: [
          'Лингвистика',
          'Педагогика',
          'Цифровые гуманитарные науки',
          'Юрислингвистика',
          'Искусственный интеллект',
          'Междисциплинарные исследования',
        ],
      }),
      f('sections', 'Секции мероприятия', 'textarea', { required: true, wide: true }),
      f('keywords', 'Ключевые слова проекта', 'text', { required: true, wide: true }),
      f('audience', 'Целевая аудитория', 'checkboxes', {
        required: true,
        wide: true,
        options: ['Исследователи', 'Преподаватели', 'Докторанты', 'Магистранты', 'Студенты', 'Практики'],
      }),
      f('languages', 'Рабочие языки', 'checkboxes', {
        required: true,
        wide: true,
        options: ['Қазақша', 'Русский', 'English'],
      }),
    ],
  },
  {
    id: 'calendar',
    title: 'Календарь и формат',
    desc: 'Даты, формат проведения и связанные сроки.',
    fields: [
      f('startAt', 'Дата и время начала', 'datetime-local', { required: true }),
      f('endAt', 'Дата и время окончания', 'datetime-local', { required: true }),
      f('timezone', 'Часовой пояс', 'select', {
        required: true,
        options: ['Asia/Qyzylorda (UTC+5)', 'UTC', 'Europe/London', 'America/New_York'],
        value: 'Asia/Qyzylorda (UTC+5)',
      }),
      f('format', 'Формат проведения', 'select', {
        required: true,
        options: ['Очно', 'Онлайн', 'Смешанный (очно + онлайн)', 'Заочно'],
      }),
      f('venue', 'Место проведения / адрес', 'text', { wide: true }),
      f('onlineLink', 'Платформа / ссылка', 'text', { wide: true }),
      f('openDate', 'Открытие приёма', 'date'),
      f('submissionDeadline', 'Дедлайн подачи', 'date'),
      f('notificationDate', 'Уведомление авторов', 'date'),
      f('programDate', 'Публикация программы', 'date'),
      f('proceedingsDate', 'Публикация сборника', 'date'),
      f('certificateDate', 'Выдача сертификатов', 'date'),
    ],
  },
  {
    id: 'materials',
    title: 'Участие и материалы',
    desc: 'Правила подачи статьи, структура, лимиты и состав авторов.',
    fields: [
      f('participationTypes', 'Типы участия', 'checkboxes', {
        required: true,
        wide: true,
        options: ['Доклад с публикацией', 'Доклад без публикации', 'Слушатель'],
      }),
      f('materialTypes', 'Типы материалов', 'checkboxes', {
        required: true,
        wide: true,
        options: ['Научная статья', 'Тезисы доклада', 'Постер', 'Презентация', 'Видео', 'Собственный вариант'],
      }),
      f('submissionMode', 'Способ подачи текста', 'select', {
        required: true,
        options: ['Структурированная веб-форма + DOCX', 'Только веб-форма', 'Только DOCX'],
      }),
      f('articleStructure', 'Структура научной статьи', 'select', {
        required: true,
        options: ['Обычная научная структура', 'IMRAD', 'Свободные разделы', 'Собственная структура'],
      }),
      f('sectionNames', 'Названия разделов', 'textarea', { wide: true }),
      f('wordRange', 'Объём статьи', 'text', { required: true, value: '3000–6000 слов' }),
      f('abstractRule', 'Аннотации', 'text', { required: true, value: 'KZ/RU/EN · 150–250 слов' }),
      f('keywordRule', 'Ключевые слова статьи', 'text', { required: true, value: '5–10' }),
      f('visualLimits', 'Таблицы и рисунки', 'text', { value: 'До 10 таблиц и до 10 рисунков' }),
      f('authorPolicy', 'Изменение авторов после подачи', 'text', {
        value: 'Только через запрос организатору',
        locked: true,
      }),
      f('team', 'Команда организаторов / редакторов', 'people', { required: true, wide: true }),
    ],
  },
  {
    id: 'review',
    title: 'Проверка и рецензирование',
    desc: 'Технические правила, тематический порог, критерии и решение.',
    fields: [
      f('technicalCheck', 'Техническая проверка', 'select', { options: ['Включена', 'Выключена'], value: 'Включена' }),
      f('citationStyle', 'Стиль ссылок', 'select', {
        required: true,
        options: [
          'ГОСТ: цифровые ссылки в квадратных скобках',
          'APA / автор–год',
          'Собственный формат',
        ],
      }),
      f('referencesRange', 'Количество источников', 'text', { value: '5–50' }),
      f('referenceMatch', 'Связь ссылок и библиографии', 'select', {
        options: ['Строгое совпадение обязательно', 'Предупреждение без блокировки'],
        value: 'Строгое совпадение обязательно',
      }),
      f('thematicCheck', 'Тематическая проверка', 'select', {
        options: ['Включена · порог 60/100', 'Выключена'],
        value: 'Включена · порог 60/100',
      }),
      f('reviewType', 'Рецензирование', 'select', {
        options: [
          'Один экспертный этап по рубрике',
          'Два независимых эксперта',
          'Редакционная проверка',
        ],
      }),
      f('criteria', 'Критерии рецензии', 'textarea', { wide: true }),
      f('decision', 'Порог решения', 'text', { wide: true }),
      f('revision', 'Доработка', 'text', { value: '1 попытка · 5 календарных дней' }),
    ],
  },
  {
    id: 'documents',
    title: 'Публикация и документы',
    desc: 'Объявление, информационное письмо, программа, сборник, справки и сертификаты.',
    fields: [
      f('announcement', 'Публикация объявления', 'checkboxes', {
        required: true,
        wide: true,
        options: ['Страница на платформе', 'Информационное письмо PDF'],
      }),
      f('letterTemplate', 'Дизайн информационного письма', 'select', {
        required: true,
        options: [
          'Последний подтвержденный шаблон организации',
          'Загрузить новый DOCX/PDF',
          'Собственный вариант',
        ],
      }),
      f('proceedings', 'Электронный сборник', 'select', {
        options: ['Да · PDF + отдельные DOCX/PDF', 'Только общий PDF', 'Нет'],
        value: 'Да · PDF + отдельные DOCX/PDF',
      }),
      f('program', 'Программа', 'select', { options: ['Да', 'Нет'], value: 'Да' }),
      f('statusLetter', 'Справка о статусе', 'select', { options: ['Да', 'Нет'], value: 'Да' }),
      f('certificate', 'Сертификат', 'select', {
        options: ['Да · QR-верификация', 'Да · без QR', 'Нет'],
        value: 'Да · QR-верификация',
      }),
      f('identifiers', 'ISBN/DOI и индексирование', 'select', {
        options: ['Не подключено', 'Собственный вариант'],
        value: 'Не подключено',
      }),
    ],
  },
  {
    id: 'finance',
    title: 'Финансовая модель',
    desc: 'Оплата отделена от научного решения и подтверждается человеком или платежным шлюзом.',
    fields: [
      f('financeModel', 'Финансовая модель', 'select', {
        required: true,
        options: ['Бесплатное участие', 'Платное участие'],
      }),
      f('paymentMoment', 'Момент оплаты', 'select', {
        options: ['После предварительного принятия', 'После финального принятия', 'При регистрации'],
      }),
      f('currency', 'Валюта', 'select', { options: ['KZT', 'USD', 'EUR'], value: 'KZT' }),
      f('paymentDays', 'Срок оплаты', 'number', { value: '5', min: 1, max: 60 }),
      f('paymentVerification', 'Подтверждение оплаты', 'select', {
        wide: true,
        options: [
          'Платежный шлюз; при отсутствии — квитанция и ручная проверка',
          'Только ручная проверка квитанции',
        ],
        value: 'Платежный шлюз; при отсутствии — квитанция и ручная проверка',
      }),
    ],
  },
  {
    id: 'preview',
    title: 'Предпросмотр и модерация',
    desc: 'Черновик не публикуется автоматически. После проверки владелец назначает проектную роль редактора.',
    fields: [],
  },
];

export const JOURNAL_STEPS: EditorialWizardStep[] = [
  {
    id: 'identity',
    title: 'Журнал и издатель',
    desc: 'Постоянная карточка журнала, издатель, главный редактор и официальные контакты.',
    fields: [
      f('projectType', 'Тип проекта', 'text', {
        value: 'Научный рецензируемый журнал',
        locked: true,
      }),
      f('titleRu', 'Полное название (RU)', 'text', { required: true, wide: true }),
      f('titleKz', 'Полное название (KZ)', 'text', { wide: true }),
      f('titleEn', 'Полное название (EN)', 'text', { wide: true }),
      f('acronym', 'Краткое название / акроним', 'text'),
      f('publisher', 'Учредитель / издатель', 'text', { required: true }),
      f('editorInChief', 'Главный редактор', 'people', { required: true, wide: true, single: true }),
      f('contacts', 'Официальные контакты', 'text', { required: true }),
    ],
  },
  {
    id: 'scope',
    title: 'Область и редакционная команда',
    desc: 'Цели, предметные области, рубрики, языки, коллегия и база рецензентов.',
    fields: [
      f('aimsScope', 'Цели и область журнала', 'textarea', { required: true, wide: true }),
      f('subjectAreas', 'Предметные области', 'checkboxes', {
        required: true,
        wide: true,
        options: [
          'Филология',
          'Лингвистика',
          'Педагогика',
          'Социальные науки',
          'Цифровые гуманитарные науки',
          'Междисциплинарные исследования',
        ],
      }),
      f('rubrics', 'Рубрики', 'textarea', { required: true, wide: true }),
      f('languages', 'Языки публикации', 'checkboxes', {
        required: true,
        wide: true,
        options: ['Қазақша', 'Русский', 'English'],
      }),
      f('editorialBoard', 'Редакционная коллегия', 'people', { required: true, wide: true, minPeople: 2 }),
      f('reviewers', 'База рецензентов', 'people', { wide: true }),
    ],
  },
  {
    id: 'issues',
    title: 'Периодичность и первый выпуск',
    desc: 'Постоянный журнал содержит отдельные сущности выпусков.',
    fields: [
      f('frequency', 'Периодичность', 'select', {
        required: true,
        options: [
          '2 выпуска в год',
          'Ежеквартально · 4 выпуска',
          '6 выпусков в год',
          '12 выпусков в год',
          'Непрерывная публикация',
        ],
      }),
      f('numbering', 'Модель нумерации', 'select', {
        required: true,
        options: ['Том + номер + год', 'Номер + год', 'Непрерывная нумерация'],
        value: 'Том + номер + год',
      }),
      f('volume', 'Том', 'number', { required: true, value: '1', min: 1, max: 999 }),
      f('issue', 'Номер', 'number', { required: true, value: '1', min: 1, max: 999 }),
      f('year', 'Год', 'number', { required: true, value: String(new Date().getFullYear()), min: 2000, max: 2100 }),
      f('intakeMode', 'Режим приёма', 'select', {
        required: true,
        wide: true,
        options: [
          'Непрерывный приём с назначением в текущий выпуск',
          'Приём только на конкретный номер',
        ],
      }),
      f('publicationDate', 'Плановая дата публикации выпуска', 'date', { required: true }),
      f('issueDeadline', 'Дедлайн текущего выпуска', 'date'),
    ],
  },
  {
    id: 'articleRules',
    title: 'Правила для авторов',
    desc: 'Типы материалов, структура статьи, объём, метаданные и библиография.',
    fields: [
      f('materialTypes', 'Типы материалов', 'checkboxes', {
        required: true,
        wide: true,
        options: ['Научная статья', 'Обзорная статья', 'Методический материал', 'Рецензия', 'Собственный вариант'],
      }),
      f('submissionMode', 'Способ подачи', 'select', {
        required: true,
        options: ['Структурированная веб-форма + DOCX', 'Только веб-форма', 'Только DOCX'],
        value: 'Структурированная веб-форма + DOCX',
      }),
      f('structure', 'Структура исследовательской статьи', 'select', {
        required: true,
        options: ['IMRAD', 'Свободная структура', 'Собственная структура'],
      }),
      f('wordRange', 'Объём статьи', 'text', { required: true, value: '4000–8000 слов' }),
      f('abstracts', 'Аннотации', 'text', { value: 'KZ/RU/EN · 150–250 слов' }),
      f('keywords', 'Ключевые слова', 'text', { value: '5–10' }),
      f('citationStyle', 'Стиль библиографии', 'select', {
        options: ['ГОСТ · цифровые ссылки', 'APA / автор–год', 'Собственный формат'],
      }),
      f('referencesRange', 'Количество источников', 'text', { value: '10–50' }),
    ],
  },
  {
    id: 'review',
    title: 'Редакционная проверка',
    desc: 'Технический скрининг, тип рецензирования, критерии, решения и раунды доработки.',
    fields: [
      f('technicalCheck', 'Техническая проверка', 'select', { options: ['Включена', 'Выключена'], value: 'Включена' }),
      f('topicScore', 'Предварительная тематическая оценка', 'select', {
        options: ['Включена · 60/100', 'Выключена'],
        value: 'Включена · 60/100',
      }),
      f('reviewType', 'Тип рецензирования', 'select', {
        required: true,
        options: ['Двойное слепое', 'Одинарное слепое', 'Открытое', 'Редакционное', 'Собственный вариант'],
      }),
      f('reviewerCount', 'Количество рецензентов', 'number', { required: true, value: '2', min: 1, max: 5 }),
      f('criteria', 'Критерии', 'textarea', { wide: true }),
      f('decision', 'Решение', 'text', { wide: true }),
      f('revisionRounds', 'Раунды доработки', 'text', { value: 'До 2 раундов · по 10 календарных дней' }),
    ],
  },
  {
    id: 'documents',
    title: 'Выпуск и документы',
    desc: 'Шаблоны статьи и выпуска, форматы, письма, справки, сертификаты и идентификаторы.',
    fields: [
      f('articleTemplate', 'Шаблон статьи', 'select', {
        options: ['Последний утвержденный шаблон журнала', 'Загрузить новый вариант'],
      }),
      f('issueTemplate', 'Шаблон выпуска', 'select', {
        options: ['Последний утвержденный шаблон выпуска', 'Загрузить новый вариант'],
      }),
      f('issueFormats', 'Форматы выпуска', 'checkboxes', {
        wide: true,
        options: ['Общий PDF', 'Отдельные PDF статей', 'Исходные DOCX статей'],
      }),
      f('toc', 'Содержание выпуска', 'select', {
        options: ['Формируется автоматически', 'Ручная сборка'],
        value: 'Формируется автоматически',
      }),
      f('letters', 'Редакционные письма и справки', 'select', { options: ['Включены', 'Выключены'], value: 'Включены' }),
      f('certificate', 'Сертификат публикации', 'select', {
        options: ['Включен · QR', 'Включен · без QR', 'Выключен'],
        value: 'Включен · QR',
      }),
      f('indexing', 'ISSN, DOI, Crossref, индексирование', 'select', {
        wide: true,
        options: ['Не подключено / заполняется владельцем', 'Собственный вариант'],
        value: 'Не подключено / заполняется владельцем',
      }),
    ],
  },
  {
    id: 'finance',
    title: 'Финансовая модель',
    desc: 'Оплата не влияет на научное решение редакции.',
    fields: [
      f('financeModel', 'Финансовая модель', 'select', {
        required: true,
        options: ['Бесплатная публикация', 'Платная публикация'],
      }),
      f('paymentMoment', 'Момент оплаты', 'select', {
        options: ['После принятия статьи', 'После публикации', 'До рецензирования'],
      }),
      f('paymentDays', 'Срок оплаты', 'number', { value: '7', min: 1, max: 60 }),
      f('currency', 'Валюта', 'select', { options: ['KZT', 'USD', 'EUR'], value: 'KZT' }),
      f('benefits', 'Льготы и освобождения', 'textarea', {
        wide: true,
        placeholder: 'Редкое поле можно перенести в расширенные настройки',
      }),
    ],
  },
  {
    id: 'preview',
    title: 'Предпросмотр и запуск',
    desc: 'Журнал и приём статей запускаются отдельными действиями после модерации.',
    fields: [],
  },
];

export function stepsForKind(kind: 'CONFERENCE' | 'JOURNAL'): EditorialWizardStep[] {
  return kind === 'CONFERENCE' ? CONFERENCE_STEPS : JOURNAL_STEPS;
}
